<template>
  <div
    class="bg-gray-800 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 w-full"
  >
    <div class="flex flex-row w-full">
      <!-- Image container -->
      <div class="w-1/3 h-40 relative flex-shrink-0">
        <img
          v-if="product.mainPicture"
          :src="getImageUrl(product.mainPicture)"
          :alt="product.name"
          class="w-full h-full object-cover"
        />
        <div
          v-else
          class="w-full h-full bg-gray-700 flex items-center justify-center"
        >
          <span class="text-gray-500">Nema slike</span>
        </div>
      </div>

      <!-- Content area -->
      <div class="p-4 flex flex-col justify-between w-2/3 flex-shrink-0">
        <div class="h-16 mb-2">
          <router-link :to="`/product/${product.slug}`" class="block">
            <h3
              class="text-sm font-semibold text-blue-400 line-clamp-2 hover:text-blue-300 transition-colors"
            >
              {{ product.name }}
            </h3>
          </router-link>
        </div>

        <div class="flex items-center justify-between w-full mb-2">
          <span class="text-green-400 font-bold truncate">
            {{ formatPrice(product.price) }} RSD
          </span>
          <div class="flex items-center justify-end">
            <button
              @click.stop.prevent="toggleFollow"
              class="flex items-center text-red-400 hover:text-red-300 transition-colors"
              :class="{ 'opacity-70': isLoading }"
              :disabled="isLoading"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 mr-1"
                viewBox="0 0 24 24"
                stroke="currentColor"
                fill="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
              <span class="text-sm">{{ product.followersCount }}</span>
            </button>
          </div>
        </div>

        <div class="flex items-center justify-between w-full">
          <div class="flex items-center text-purple-400 truncate">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 mr-1"
              viewBox="0 0 24 24"
              stroke="currentColor"
              fill="none"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span class="text-sm truncate">{{ product.city }}</span>
          </div>
          <router-link
            :to="`/profile/${product.userId || ''}`"
            class="text-sm text-purple-400 hover:text-purple-300 transition-colors truncate"
          >
            {{ product.username }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from 'vue';
import { getImageUrl } from '@/utils/imageUtils'; // DODATO

interface Product {
  id: string;
  name: string;
  slug: string;
  price: string;
  mainPicture: string | null;
  followersCount: number;
  city: string;
  username: string;
  userId?: string;
  category?: {
    id: string;
    name: string;
  };
}

export default defineComponent({
  name: 'ProductCard',
  props: {
    product: {
      type: Object as PropType<Product>,
      required: true,
    },
    showFollowButton: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['unfollow'],
  setup(props, { emit }) {
    const isLoading = ref(false);

    const formatPrice = (price: string): string => {
      return parseFloat(price).toLocaleString('sr-RS', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    // ZAMENIO STARU FUNKCIJU SA NOVOM
    const getImageUrlForTemplate = (imagePath: string): string => {
      return getImageUrl(imagePath);
    };

    const toggleFollow = async (event: Event) => {
      // Prevent event propagation
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (isLoading.value) return;

      isLoading.value = true;

      try {
        emit('unfollow', props.product.id);
      } catch (error) {
        console.error('Error unfollowing product:', error);
      } finally {
        isLoading.value = false;
      }
    };

    return {
      isLoading,
      formatPrice,
      getImageUrl: getImageUrlForTemplate, // KORISTIMO NOVU FUNKCIJU
      toggleFollow,
    };
  },
});
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

h3 {
  font-size: 1rem; /* Smanjena veličina fonta */
  line-height: 1.2; /* Manji razmak između linija */
}
</style>
